import type { Product } from '../types/products';
import { company } from './company';

const guideSheet = {
  fileName: company.productGuide.fileName,
  fileUrl: company.productGuide.fileUrl,
};

export const products: Product[] = [
  // ─────────────────────────────  Petroleum Sulphonates  ─────────────────────────────
  {
    id: 'daxol-na',
    code: 'DAXOL-Na',
    name: 'DAXOL-Na Sodium Petroleum Sulphonate',
    categorySlug: 'petroleum-sulphonates',
    group: 'Sodium Sulphonates',
    description:
      'Our line of DAXOL-Na sodium petroleum sulphonates varies in average equivalent weight from 420 to 480. DAXOL-Na is a transparent, dark brown liquid that is readily soluble in mineral oil, delivering outstanding emulsion quality and corrosion resistance in customer formulations.',
    keyFeatures: [
      'Broad average equivalent weight distribution (420–480)',
      'Outstanding emulsion performance',
      'Excellent rust prevention',
      'High storage stability',
      'High compatibility with base stocks',
      'Consistent, globally registered quality',
    ],
    applications: [
      'Metalworking emulsifiers & demulsifiers',
      'Oilfield flooding compounds & flocculants',
      'Crude oil emulsifiers',
      'Ore, phosphate & quartz sand flotation',
      'Industrial rust preventatives & detergents',
    ],
    typicalProperties: [
      { property: 'Average Equivalent Weight', method: 'ASTM D3712', value: '420–480' },
      { property: 'Appearance', value: 'Transparent dark brown liquid' },
      { property: 'Solubility', value: 'Readily soluble in mineral oil' },
    ],
    specTable: [
      { property: 'Sodium Sulfonate, wt%', method: 'ASTM D3712', value: 'min 60' },
      { property: 'Oil, wt%', method: 'ASTM D3712', value: 'max 37' },
      { property: 'Average Equivalent Weight', method: 'ASTM D3712', value: '420–480' },
      { property: 'Inorganic Salt, wt%', method: 'ASTM D3712', value: 'max 1.0' },
      { property: 'Free Alkalinity, mg KOH/g', method: 'SAM 150', value: 'max 0.25' },
      { property: 'Water, wt%', method: 'ASTM D95', value: 'max 5' },
      { property: 'Color (dilute)', method: 'ASTM D1500', value: 'max 5.0' },
      { property: 'Flash Point, COC, °C', method: 'ASTM D92', value: '>180' },
      { property: 'Density @ 20°C, kg/m³', method: 'ASTM D4052', value: '1010' },
    ],
    specSheet: guideSheet,
  },
  {
    id: 'daxol-ca',
    code: 'DAXOL-CA',
    name: 'DAXOL-CA Calcium Petroleum Sulphonate',
    categorySlug: 'petroleum-sulphonates',
    group: 'Calcium Sulphonates',
    description:
      'DAXOL-CA is a reliable rust inhibitor for metalworking specialties, greases, rust-preventive fluids, and petroleum and synthetic lubricants — and as an additive designed for water-based applications such as dewatering. DAXOL-CA offers ideal corrosion inhibition.',
    keyFeatures: [
      'Ideal corrosion inhibition',
      'Excellent detergency',
      'High thermal stability',
      'Suitable for greases & rust-preventive fluids',
      'Effective in water-based dewatering applications',
    ],
    applications: [
      'Metalworking specialties',
      'Greases',
      'Rust-preventive fluids',
      'Petroleum & synthetic lubricants',
      'Water-based dewatering',
    ],
    typicalProperties: [
      { property: 'Appearance', value: 'Dark brown viscous liquid' },
      { property: 'Active Matter % by Wt.', value: '40–50' },
      { property: 'Total Base Number, mg KOH/g', method: 'ASTM D664', value: '<30' },
    ],
    specTable: [
      { property: 'Appearance', value: 'Dark brown viscous liquid' },
      { property: 'Calcium Content, % by Wt.', value: '2–2.5' },
      { property: 'Active Matter, % by Wt.', value: '40–50' },
      { property: 'Oil Content, % by Wt.', value: '50–60' },
      { property: 'Water Content, % by Wt.', value: '0–0.50' },
      { property: 'Inorganic Salt, % by Wt.', value: '<1' },
      { property: 'Viscosity @ 40°C', method: 'ASTM D445', value: '5000 cSt' },
      { property: 'Viscosity @ 100°C', method: 'ASTM D445', value: '75 cSt' },
      { property: 'Density @ 15.6°C, g/ml', method: 'ASTM D4052', value: '0.98' },
      { property: 'Flash Point, COC, °C', method: 'ASTM D92', value: '165' },
      { property: 'Total Base Number, mg KOH/g', method: 'ASTM D664', value: '<30' },
      { property: 'Average Molecular Weight (Typical)', value: '900' },
    ],
    specSheet: guideSheet,
  },

  // ─────────────────────────────  Metalworking Fluids  ─────────────────────────────
  {
    id: 'dax-sc8218',
    code: 'DAX-SC8218',
    name: 'Soluble Cutting Oil Emulsifier',
    categorySlug: 'metalworking-fluids',
    group: 'Soluble Cutting Oil Emulsifiers',
    description:
      'High-performance emulsifier package for water-soluble cutting oils, delivering stable emulsions with excellent corrosion protection and built-in biocide and defoamer.',
    keyFeatures: [
      'Excellent corrosion protection',
      'Built-in biocide and defoamer',
      'Compatible with Group I and II base oils',
      'Meets IS 1115 standards',
    ],
    applications: ['Water-soluble cutting oils', 'General machining', 'Grinding fluids'],
    typicalProperties: [
      { property: 'Color', value: 'Amber to dark brown' },
      { property: 'Appearance', value: 'Viscous liquid' },
      { property: 'pH (5% in distilled water)', value: 'Min 9.5' },
      { property: 'Acid value', value: 'Max 10 mg KOH/g' },
      { property: 'Amine value', value: 'Max 70 mg KOH/g' },
      { property: 'Oil solubility (18% in Group I & II)', value: 'Bright and clear' },
      { property: 'Thermal stability at 50°C', value: 'No separation' },
    ],
    specSheet: guideSheet,
  },
  {
    id: 'dax-scoss50',
    code: 'DAX-SCOSS50',
    name: 'Semi-Synthetic Concentrate — Long Service Life',
    categorySlug: 'metalworking-fluids',
    group: 'Semi-Synthetic Metalworking Fluids',
    description:
      'Semi-synthetic package for long service life in metalworking fluids, engineered for stability and dependable corrosion protection.',
    keyFeatures: [
      'Excellent corrosion protection',
      'Self-emulsifying',
      'Biodegradable',
      'Excellent hard water stability',
      'Cost-effective',
    ],
    applications: ['Semi-synthetic metalworking fluids', 'Machining', 'Grinding'],
    typicalProperties: [
      { property: 'Color', value: 'Amber to dark brown' },
      { property: 'Appearance', value: 'Viscous liquid' },
      { property: 'pH (5% in distilled water)', value: 'Min 9.5' },
      { property: 'Acid value', value: 'Max 10 mg KOH/g' },
      { property: 'Amine value', value: 'Max 70 mg KOH/g' },
      { property: 'Oil solubility (18% in Group I & II)', value: 'Bright and clear' },
      { property: 'Thermal stability at 50°C', value: 'No separation' },
    ],
    specSheet: guideSheet,
  },
  {
    id: 'dax-spos60',
    code: 'DAX-SPOS60',
    name: 'Semi-Synthetic Concentrate — Emulsion Stability',
    categorySlug: 'metalworking-fluids',
    group: 'Semi-Synthetic Metalworking Fluids',
    description:
      'Semi-synthetic package ensuring stability in concentrates, blends and emulsions for demanding metalworking operations.',
    keyFeatures: [
      'Excellent corrosion protection',
      'Self-emulsifying',
      'Biodegradable',
      'Excellent hard water stability',
      'Cost-effective',
    ],
    applications: ['Semi-synthetic concentrates', 'Blends & emulsions', 'Machining'],
    typicalProperties: [
      { property: 'Color', value: 'Amber to dark brown' },
      { property: 'Appearance', value: 'Viscous liquid' },
      { property: 'pH (5% in distilled water)', value: 'Min 9.5' },
      { property: 'Acid value', value: 'Max 10 mg KOH/g' },
      { property: 'Amine value', value: 'Max 70 mg KOH/g' },
      { property: 'Oil solubility (18% in Group I & II)', value: 'Bright and clear' },
      { property: 'Thermal stability at 50°C', value: 'No separation' },
    ],
    specSheet: guideSheet,
  },
  {
    id: 'dax-spwss50',
    code: 'DAX-SPWSS50',
    name: 'Semi-Synthetic Concentrate — Enhanced Stability',
    categorySlug: 'metalworking-fluids',
    group: 'Semi-Synthetic Metalworking Fluids',
    description:
      'Semi-synthetic package for long-life metalworking fluids with enhanced stability under demanding conditions.',
    keyFeatures: [
      'Excellent corrosion protection',
      'Self-emulsifying',
      'Biodegradable',
      'Excellent hard water stability',
      'Cost-effective',
    ],
    applications: ['Long-life metalworking fluids', 'Machining', 'Grinding'],
    typicalProperties: [
      { property: 'Color', value: 'Amber to dark brown' },
      { property: 'Appearance', value: 'Viscous liquid' },
      { property: 'pH (5% in distilled water)', value: 'Min 9.5' },
      { property: 'Acid value', value: 'Max 10 mg KOH/g' },
      { property: 'Amine value', value: 'Max 70 mg KOH/g' },
      { property: 'Oil solubility (18% in Group I & II)', value: 'Bright and clear' },
      { property: 'Thermal stability at 50°C', value: 'No separation' },
    ],
    specSheet: guideSheet,
  },
  {
    id: 'dax-spnps10',
    code: 'DAX-SPNPS10',
    name: 'Neat Cutting Oil — Staining Type',
    categorySlug: 'metalworking-fluids',
    group: 'Neat Cutting Oils',
    description:
      'Premium staining-type neat cutting oil with synthetic esters and staining EP additives for excellent lubrication under extreme pressure.',
    keyFeatures: [
      'Excellent lubrication',
      'Extreme pressure (EP) characteristics',
      'Low misting',
      'Low viscosity',
      'Good chip formation',
    ],
    applications: ['Heavy-duty machining', 'Broaching', 'Gear cutting', 'Threading'],
    typicalProperties: [
      { property: 'Color', value: 'Amber to dark brown' },
      { property: 'Appearance', value: 'Clear liquid' },
      { property: 'Density at 30°C', value: '0.90–0.99 g/cm³' },
      { property: 'Flash point', value: 'Min 180°C' },
    ],
    specSheet: guideSheet,
  },
  {
    id: 'dax-spnpns10',
    code: 'DAX-SPNPNS10',
    name: 'Neat Cutting Oil — Non-Staining Type',
    categorySlug: 'metalworking-fluids',
    group: 'Neat Cutting Oils',
    description:
      'Premium non-staining neat cutting oil, free from heavy metals, with synthetic esters and non-staining EP additives for enhanced visibility.',
    keyFeatures: [
      'Excellent lubrication',
      'Extreme pressure (EP) characteristics',
      'Low misting',
      'Low viscosity',
      'Enhanced visibility (non-staining)',
      'Free from heavy metals',
    ],
    applications: ['Precision machining', 'Non-ferrous metals', 'Broaching', 'Threading'],
    typicalProperties: [
      { property: 'Color', value: 'Amber to dark brown' },
      { property: 'Appearance', value: 'Clear liquid' },
      { property: 'Density at 30°C', value: '0.90–0.99 g/cm³' },
      { property: 'Flash point', value: 'Min 180°C' },
    ],
    specSheet: guideSheet,
  },
  {
    id: 'dax-spafr15',
    code: 'DAX-SPAFR15',
    name: 'Rust Preventive — Acid Fume Resistant',
    categorySlug: 'metalworking-fluids',
    group: 'Rust Preventive Additives',
    description:
      'Additive with oxidized waxes for humid and acid-fume environments, providing excellent corrosion protection with quick drying.',
    keyFeatures: [
      'Excellent corrosion protection',
      'Cost-effective',
      'Superior dewatering properties',
      'Protection in salt spray and humidity conditions',
      'Quick drying time',
    ],
    applications: ['Humid & acid-fume storage', 'Interim protection', 'Metal components'],
    typicalProperties: [
      { property: 'Color', value: 'Hazy dark brown or light brown' },
      { property: 'Appearance', value: 'Liquid or paste' },
      { property: 'Acid value', value: 'Max 20 mg KOH/g' },
      { property: 'Solubility (10% in MTO)', value: 'Bright and clear' },
    ],
    specSheet: guideSheet,
  },
  {
    id: 'dax-sprpd50',
    code: 'DAX-SPRPD50',
    name: 'Rust Preventive — Resin, Quick-Dry',
    categorySlug: 'metalworking-fluids',
    group: 'Rust Preventive Additives',
    description:
      'Additive with resins for enhanced salt-spray performance and quick drying, delivering durable corrosion protection.',
    keyFeatures: [
      'Excellent corrosion protection',
      'Cost-effective',
      'Superior dewatering properties',
      'Enhanced salt spray performance',
      'Quick drying time',
    ],
    applications: ['Salt-spray protection', 'Component preservation', 'Metal storage'],
    typicalProperties: [
      { property: 'Color', value: 'Hazy dark brown or light brown' },
      { property: 'Appearance', value: 'Liquid or paste' },
      { property: 'Acid value', value: 'Max 20 mg KOH/g' },
      { property: 'Solubility (10% in MTO)', value: 'Bright and clear' },
      { property: 'Solid content at 150°C', value: 'Min 50%' },
    ],
    specSheet: guideSheet,
  },
  {
    id: 'dax-spsdd15',
    code: 'DAX-SPSDD15',
    name: 'Rust Preventive — Double Oxidized Wax',
    categorySlug: 'metalworking-fluids',
    group: 'Rust Preventive Additives',
    description:
      'Additive with double-oxidized waxes for reliable salt-spray and humidity protection of finished metal parts.',
    keyFeatures: [
      'Excellent corrosion protection',
      'Cost-effective',
      'Superior dewatering properties',
      'Salt spray and humidity protection',
    ],
    applications: ['Humidity protection', 'Salt-spray environments', 'Metal finishing'],
    typicalProperties: [
      { property: 'Color', value: 'Hazy dark brown or light brown' },
      { property: 'Appearance', value: 'Liquid or paste' },
      { property: 'Acid value', value: 'Max 20 mg KOH/g' },
      { property: 'Solubility (10% in MTO)', value: 'Bright and clear' },
    ],
    specSheet: guideSheet,
  },
  {
    id: 'dax-spspw',
    code: 'DAX-SPSPW',
    name: 'Rust Preventive — Flexible Paste',
    categorySlug: 'metalworking-fluids',
    group: 'Rust Preventive Additives',
    description:
      'Flexible paste-form additive with dosing options for varying protection levels across diverse storage conditions.',
    keyFeatures: [
      'Excellent corrosion protection',
      'Cost-effective',
      'Superior dewatering properties',
      'Dosing flexibility',
    ],
    applications: ['Adjustable-protection coatings', 'Long-term storage', 'Metal components'],
    typicalProperties: [
      { property: 'Color', value: 'Hazy dark brown or light brown' },
      { property: 'Appearance', value: 'Paste' },
      { property: 'Acid value', value: 'Max 20 mg KOH/g' },
      { property: 'Solubility (10% in MTO)', value: 'Bright and clear' },
      { property: 'Drop point', value: '35–45°C' },
    ],
    specSheet: guideSheet,
  },
  {
    id: 'dax-spsopw',
    code: 'DAX-SPSOPW',
    name: 'Rust Preventive — High-Performance',
    categorySlug: 'metalworking-fluids',
    group: 'Rust Preventive Additives',
    description:
      'High-performance additive for extended protection in harsh environments where durability is critical.',
    keyFeatures: [
      'Excellent corrosion protection',
      'Cost-effective',
      'Superior dewatering properties',
      'Extended protection in harsh environments',
    ],
    applications: ['Harsh-environment storage', 'Marine transport', 'Long-term preservation'],
    typicalProperties: [
      { property: 'Color', value: 'Hazy dark brown or light brown' },
      { property: 'Appearance', value: 'Liquid or paste' },
      { property: 'Acid value', value: 'Max 20 mg KOH/g' },
      { property: 'Solubility (10% in MTO)', value: 'Bright and clear' },
    ],
    specSheet: guideSheet,
  },
  {
    id: 'dax-spsow',
    code: 'DAX-SPSOW',
    name: 'Rust Preventive — Oxidized Wax',
    categorySlug: 'metalworking-fluids',
    group: 'Rust Preventive Additives',
    description:
      'Additive with oxidized waxes for effective salt-spray and humidity protection at a competitive cost.',
    keyFeatures: [
      'Excellent corrosion protection',
      'Cost-effective',
      'Superior dewatering properties',
      'Salt spray and humidity protection',
    ],
    applications: ['General rust prevention', 'Salt-spray environments', 'Metal storage'],
    typicalProperties: [
      { property: 'Color', value: 'Hazy dark brown or light brown' },
      { property: 'Appearance', value: 'Liquid or paste' },
      { property: 'Acid value', value: 'Max 20 mg KOH/g' },
      { property: 'Solubility (10% in MTO)', value: 'Bright and clear' },
    ],
    specSheet: guideSheet,
  },

  // ─────────────────────────────  Textile Additives  ─────────────────────────────
  {
    id: 'dax-sptex91',
    code: 'DAX-SPTEX91',
    name: 'Knitting Oil Emulsifier',
    categorySlug: 'textile-additives',
    group: 'Knitting Oil Emulsifiers',
    description:
      'Knitting oil emulsifier with enhanced corrosion protection for high-speed knitting processes, reducing needle wear while remaining easy to scour.',
    keyFeatures: [
      'Excellent corrosion protection',
      'Self-emulsifying',
      'Biodegradable',
      'Reduces needle wear in high-speed knitting',
      'Cost-effective',
    ],
    applications: ['High-speed knitting oils', 'Needle lubrication', 'Textile spin finishes'],
    typicalProperties: [
      { property: 'Color', value: 'Pale yellow to amber' },
      { property: 'Appearance', value: 'Viscous liquid' },
      { property: 'pH (5% in distilled water)', value: '7–9' },
      { property: 'Acid value', value: 'Max 5 mg KOH/g' },
      { property: 'Specific gravity at 30°C', value: '0.9–1.0' },
      { property: 'Oil solubility (12% in Group II)', value: 'Bright and clear' },
    ],
    specSheet: guideSheet,
  },
  {
    id: 'dax-sptexcoe10',
    code: 'DAX-SPTEXCOE10',
    name: 'Coning Oil Emulsifier',
    categorySlug: 'textile-additives',
    group: 'Coning Oil Emulsifiers',
    description:
      'Coning oil emulsifier for textile applications with non-ionic surfactants and ester chemistry — adjusting viscosity to reduce friction and break levels while providing powerful static protection.',
    keyFeatures: [
      'Adjusts viscosity to reduce friction and break levels',
      'Powerful static protection at low levels',
      'Compatible with weaving sizes',
      'Excellent scour ability for level dyeing',
    ],
    applications: ['Coning oils', 'Weaving preparation', 'Level dyeing', 'Yarn finishing'],
    typicalProperties: [
      { property: 'Color', value: 'Yellow to amber' },
      { property: 'Appearance', value: 'Viscous liquid' },
      { property: 'pH (5% in distilled water)', value: '7–9' },
      { property: 'Acid value', value: 'Max 5 mg KOH/g' },
      { property: 'Specific gravity at 30°C', value: '0.9–1.0' },
      { property: 'Oil solubility (12% in Group II)', value: 'Bright and clear' },
    ],
    specSheet: guideSheet,
  },
];

export const getProductsByCategory = (categorySlug: string): Product[] =>
  products.filter((p) => p.categorySlug === categorySlug);

export const getProductById = (id: string): Product | undefined =>
  products.find((p) => p.id === id);

/** Unique group names within a category, preserving data order. */
export const getGroupsByCategory = (categorySlug: string): string[] => {
  const groups: string[] = [];
  for (const p of getProductsByCategory(categorySlug)) {
    if (!groups.includes(p.group)) groups.push(p.group);
  }
  return groups;
};
